import {useRef, useEffect, useState} from 'react'
import dayjs, { type Dayjs } from 'dayjs'
import {parseISO, addDays, /*addMonths, subDays, differenceInMonths,*/ differenceInDays} from 'date-fns'
import {Divider} from 'antd'
import commonStyles from '../styles.module.scss'
import {
  Form,
  FormItem,
  InputAmount,
  Box,
  Button,
  Text,
  useForm,
  useWatch,
} from 'common/components'
import {AntdDatePicker} from 'common/components/inputs/DatePicker'
import {FIELD_RULES} from 'common/constants'
import {isItemDrawdown, formatDate} from 'common/helpers'
import {ForecastFormItem} from 'common/types'
import {useUpdateRequest, useAcceptRequestStatus} from '..'

interface RequestOpenFormProps {
  forecast: ForecastFormItem[]
  request: any
}

interface DateSelectProps {
  name: string
  label: string
  date: Date
  disabledDate: any
  disabled?: boolean
}

const isDay2Day = (openDay:Dayjs, matuDay:Dayjs) => (
  ( openDay.date() === matuDay.date() ) ||
  (
    openDay.isSame(openDay.endOf('month'), 'day') &&
    matuDay.isSame(matuDay.endOf('month'), 'day')
  )
)
const getDay2Day = (openDay:Dayjs, monthsCount:number) => {
  // if ( openDay.isSame(openDay.endOf('month'), 'day') ) {
  //   return openDay.startOf('month').add(monthsCount, 'month').endOf('month')
  // }
  // else
  const matuDay = openDay.add(monthsCount, 'month')
  // console.log(openDay.format('YYYY-MM-DD'), monthsCount)
  // console.log(matuDay.format('YYYY-MM-DD'))
  return matuDay
}

export const calcMaturityDate = (openDate:Date, maturityDate:Date, newOpenDate:Date) => {
  const openDay = dayjs(openDate)
  const matuDay = dayjs(maturityDate)
  const newDay  = dayjs(newOpenDate)
  if (
    !openDate      || !openDay.isValid() ||
    !maturityDate  || !matuDay.isValid() ||
    !newOpenDate   || !newDay.isValid()
  ) {
    return undefined
  }
  // console.log(dayjs(newOpenDate).isSame(today, 'day'))
  // console.log(dayjs(newOpenDate).isSame(openDay, 'day'))
  // console.log(openDay.toString(), openDay.date(), matuDay.toString(), matuDay.date())
  // console.log(newDay.month(), newDay.date())
  if (
    dayjs(newOpenDate).isSame(openDay, 'day') ||
    !isDay2Day(openDay, matuDay)
  ) {
    const daysCount = differenceInDays(maturityDate, openDate)
    return addDays(newOpenDate, daysCount)
  } else {
    // console.log(openDay.format('YYYY-MM-DD'))
    // console.log(matuDay.format('YYYY-MM-DD'))
    // console.log(newDay.format('YYYY-MM-DD'))
    const result = getDay2Day(newDay, matuDay.diff(openDay, 'month'))
    // console.log(result.format('YYYY-MM-DD'))
    return result.toDate()
  }
}

export default function RequestOpenForm({
  forecast,
  request,
}: RequestOpenFormProps) {
  const initialDrawdown = forecast.find((item) => isItemDrawdown(item))
  const {
    mutate: acceptRequestStatus,
    isMutating: isAcceptRequestStatusMutating,
  } = useAcceptRequestStatus(request.uuid)
  const {mutate: updateRequest, isMutating: isUpdateRequestMutating} =
    useUpdateRequest(request.uuid, acceptRequestStatus)
  const {current: today} = useRef(new Date())
  const [form] = useForm()

  const todayNext = addDays(today, 1)
  const todayNextZero =  new Date(todayNext.getFullYear(), todayNext.getMonth(), todayNext.getDate());
  const openDate = parseISO(request.openDate)
  const maturityDate = parseISO(request.maturityDate)
  const openDateValue = useWatch('openDate', form)
  const maturityDateValue = useWatch('maturityDate', form)
  
  useEffect(() => {
    const maturityDateNew = calcMaturityDate(openDate, maturityDate, openDateValue)
    if (!maturityDateNew) return
    form.setFieldValue('maturityDate', maturityDateNew)
  }, [openDateValue]) // eslint-disable-line

  useEffect(() => {
    form.setFieldValue('openDate', dayjs().toDate())
    // calcMaturityDate(
    //   dayjs('2019-12-27').toDate(),
    //   dayjs('2020-02-27').toDate(),
    //   dayjs('2019-12-31').toDate(),
    // )
  }, [form])

  const handleFinish = ({openDate, maturityDate, ...values}: any) => {
    const newValues = {
      ...values,
      openDate: formatDate(openDate, 'server'),
      maturityDate: formatDate(maturityDate, 'server'),
    }
    updateRequest(newValues)
  }
 
  const initialValues: any = {
    applicationFeeAmount: request.applicationFeeAmount,
    brokerageFeeAmount: request.brokerageFeeAmount,
    legalFeeAmount: request.legalFeeAmount,
    initialDrawdown: initialDrawdown.amount,
  }

  return (
    <Form
      form={form}
      className={commonStyles.requestForm}
      initialValues={initialValues}
      onFinish={handleFinish}
    >
      <div className={commonStyles.openFormFields}>
        <FormItem
          name="applicationFeeAmount"
          label="Application fee"
          rules={FIELD_RULES}
        >
          <InputAmount min={0} />
        </FormItem>
        <FormItem
          name="brokerageFeeAmount"
          label="Broker fee"
          rules={FIELD_RULES}
        >
          <InputAmount min={0} />
        </FormItem>
        <FormItem name="legalFeeAmount" label="Legal fee" rules={FIELD_RULES}>
          <InputAmount min={0} />
        </FormItem>
        <FormItem
          name="initialDrawdown"
          label="Initial drawdown"
          rules={FIELD_RULES}
        >
          <InputAmount min={0} />
        </FormItem>
      </div>
      <Divider className={commonStyles.divider} />
      <div className={commonStyles.openFormDates}>
        <DateSelect
          name="openDate"
          label="Loan open date"
          date={openDateValue}
          disabledDate={(date: Date) =>
            date > todayNextZero // || addDays(date, daysCount) <= today
          }
        />
        <DateSelect
          name="maturityDate"
          label="Loan maturity date"
          date={maturityDateValue}
          disabledDate={() => true }
          disabled={true}
        />
      </div>
      <Box align="center" mt={24}>
        <Button
          htmlType="submit"
          loading={isUpdateRequestMutating || isAcceptRequestStatusMutating}
        >
          Confirm & open
        </Button>
      </Box>
    </Form>
  )
}

const DateSelect = ({name, label, date, disabledDate, disabled}: DateSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const handleOpen = () => setIsOpen(true)

  return (
    <Box align="center">
      <Text weight="medium" color="greenLight">
        {label}
      </Text>
      <Button
        dropdown
        className={commonStyles.dateButton}
        variant="text"
        open={isOpen}
        onClick={!disabled ? handleOpen:() => {}}
      >
        <Text weight="medium" size="3xl" color={!disabled ? "black":"grayer50"}>
          {date ? formatDate(date, 'fifth') : '...'}
        </Text>
        <FormItem noStyle name={name} rules={FIELD_RULES}>
          <AntdDatePicker
            inputReadOnly
            className={commonStyles.datePicker}
            mode="date"
            showToday={false}
            allowClear={false}
            open={isOpen}
            disabledDate={!disabled ? disabledDate : () => false}
            disabled={!!disabled}
            onOpenChange={setIsOpen}
          />
        </FormItem>
      </Button>
    </Box>
  )
}
